import { useQuery } from "@/api/utils/useQuery"
import { HasuraQueries } from "@/api/hasura/queries"
import { QueryResult } from "@/api/utils/types"
import { ServiceConfigParser } from "@/models/admin/serviceConfig/serviceConfig.parser"
import { IServiceConfig } from "@/models/admin/serviceConfig/serviceConfig.types"
import {
  GetBeFinancialsConfigListQuery,
  GetBeFinancialsConfigStatsQuery,
  GetBeFinancialsConfigHistoryQuery,
} from "@/api/hasura/generated/graphql"

type ConfigListRawResponse = GetBeFinancialsConfigListQuery
type ConfigStatsRawResponse = GetBeFinancialsConfigStatsQuery
type ConfigHistoryRawResponse = GetBeFinancialsConfigHistoryQuery

export type ConfigStats = {
  totalCount: number
  activeCount: number
}

// A page of config-history versions plus the total number of versions that
// exist for the config name, so the table can paginate server-side.
export type ConfigHistoryResult = {
  records: IServiceConfig[]
  totalCount: number
}

// A configurations-list row: the currently active config plus the total number
// of records (versions) that exist for that config name.
export type ConfigListRow = IServiceConfig & { totalCount: number }

class ServiceConfigServiceClass {
  getConfigs(): QueryResult<ConfigListRow[]> {
    return useQuery({
      ...HasuraQueries.serviceConfig.all(),
      useRawResponse: true,
      placeholderData: [],
      parserFn: (data: ConfigListRawResponse): ConfigListRow[] => {
        const versionsByName = new Map<string, IServiceConfig[]>()
        data.financials_configuration.forEach((item) => {
          const config = ServiceConfigParser.toDomain(item)
          const versions = versionsByName.get(config.name) ?? []
          versions.push(config)
          versionsByName.set(config.name, versions)
        })

        const rows: ConfigListRow[] = []
        versionsByName.forEach((versions) => {
          const active = versions.find((version) => version.isActive)
          if (!active) return
          rows.push(Object.assign(active, { totalCount: versions.length }))
        })

        return rows.sort((a, b) => a.name.localeCompare(b.name))
      },
    })
  }

  getStats(): QueryResult<ConfigStats> {
    return useQuery({
      ...HasuraQueries.serviceConfig.stats(),
      useRawResponse: true,
      placeholderData: { totalCount: 0, activeCount: 0 },
      parserFn: (data: ConfigStatsRawResponse): ConfigStats => ({
        totalCount: data.total?.aggregate?.count ?? 0,
        activeCount: data.active?.aggregate?.count ?? 0,
      }),
    })
  }

  getHistory(
    name: string,
    page: number,
    pageSize: number,
  ): QueryResult<ConfigHistoryResult> {
    return useQuery({
      ...HasuraQueries.serviceConfig.history(
        name,
        pageSize,
        (page - 1) * pageSize,
      ),
      useRawResponse: true,
      placeholderData: { records: [], totalCount: 0 },
      parserFn: (data: ConfigHistoryRawResponse): ConfigHistoryResult => ({
        records: data.financials_configuration.map((item) =>
          ServiceConfigParser.toDomain(item),
        ),
        totalCount:
          data.financials_configuration_aggregate.aggregate?.count ?? 0,
      }),
    })
  }
}

export const ServiceConfigService = new ServiceConfigServiceClass()
